import { useQuery } from '@tanstack/react-query'
import { UNIFIED_DOORS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import { useMemo } from 'react'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { getTargetType } from './targetTypeMap'
import { adaptUnifiedListParams } from './unifiedResponseAdapter'
import { adaptUnifiedDoorItemForVertical } from './unifiedDoorResponseAdapter'

/**
 * Restaurant doors (DoorsTableComponentV2) flag-gated unified swap (FIR-6995).
 *
 * The door analog of `useUnifiedChainsTable`. The restaurant-doors table is the
 * one door table that does NOT use `DECommonTable` — it has a bespoke component
 * (`DoorsTableComponentV2`) with elastic name/menu search, a product selector,
 * a rich LTV summary header (`total_ltv`, `places_count`), and a column set
 * (`DoorsTableColumnsV2`) far wider than what the unified doors endpoint
 * provides. So instead of the generic `useUnifiedDoorsTableActions` hook, this
 * returns the pieces the bespoke component needs to splice the unified data
 * source in behind `enable_unified_opps_endpoint`:
 *
 *  - `enabled`: whether the unified path is active (the flag is on).
 *  - `getListAction()`: returns a bound list fetcher matching
 *    `getDoorsWithCRMReports`'s `(params, signal) => Promise<{ results }>` shape
 *    (the returned closure takes `signal` per-call and forwards it to the API),
 *    with each row re-nested under `restaurant` (`restaurant.firstbite_id` =
 *    unified `detail_id`, `restaurant.<segment>` from the provider), and
 *    `chain_proxy.id` / `door_proxy_id` set so the existing columns, detail
 *    links, selection (keyed off `restaurant.firstbite_id`), and row actions
 *    (`chain_proxy.id`, `door_proxy_id`) resolve.
 *  - `unifiedCount`: the unified door count for the current filters, to drive
 *    pagination + `setTotalRowsInBackend` so paging matches the unified rows.
 *
 * The rich summary header fields (LTV, places) and the V2-only columns the
 * unified endpoint does not feed (LTV rollups, instagram, address demographics,
 * CRM-report columns) are intentionally left to the component's / cells'
 * existing `?? 0` / `'-'` fallbacks when the flag is on — the table renders
 * blanks for them rather than crashing. Count is overridden so it stays
 * consistent with the rows. All of this is tracked for browser QA.
 */

// DoorsTableColumnsV2 nests door detail under `restaurant` and reads
// `restaurant.firstbite_id` (= unified `detail_id`) for the detail link +
// selection; row actions key off `chain_proxy.id` and `door_proxy_id`.
const RESTAURANT_DOOR_ACCESSOR = 'restaurant'

export function useUnifiedDoorsReportsTable(
  filterParams: Record<string, any>,
  // Gate for the summary query, mirroring the legacy summary's fetch condition
  // (doors tab active or a chain filter present). Prevents the flag-on summary
  // from firing/forwarding stats for a tab the user isn't viewing.
  summaryEnabled = true
): {
  enabled: boolean
  getListAction: () =>
    | ((
        params: Record<string, any>,
        signal?: AbortSignal
      ) => Promise<{ results: any[] }>)
    | null
  unifiedCount: number | undefined
  unifiedChainsCount: number | undefined
  unifiedLtv: number | undefined
  unifiedSummaryFetching: boolean
} {
  const api = useApiService()
  const { enableUnifiedOppsEndpoint } = useFeatureFlagService()
  const enabled = !!enableUnifiedOppsEndpoint

  const listAction = useMemo(() => {
    if (!enabled) return null
    const targetType = getTargetType('restaurant')
    return async (params: Record<string, any>, signal?: AbortSignal) => {
      const res = await api.getOpportunityDoorsList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) => {
          const row = adaptUnifiedDoorItemForVertical(
            item as Record<string, unknown>,
            {
              accessor: RESTAURANT_DOOR_ACCESSOR,
              // DoorsTableColumnsV2 detail link + DoorsTableComponentV2
              // selection read `restaurant.firstbite_id`, not `restaurant.id`.
              detailLinkIdField: 'firstbite_id',
            }
          )
          // V2 keys door-level actions off a top-level `door_proxy_id`
          // (DECommonTable door tables read `id`); set both so selection,
          // tag/distributor/GPO assignment, and reminders resolve.
          return { ...row, door_proxy_id: row['id'] }
        }),
      } as { results: any[] }
    }
  }, [enabled, api])

  const { data: unifiedSummary, isFetching: unifiedSummaryFetching } = useQuery(
    {
      // Match the legacy `useGetDoorReportsSummary` summary query, which uses the
      // default (finite, 0) staleTime so the count refetches after mutations.
      // Do NOT set `staleTime: Infinity` here: mutations invalidate the legacy
      // summary key, not this unified key, so an infinite staleTime would leave
      // the count stale until a hard reload.
      enabled: enabled && summaryEnabled,
      queryKey: [UNIFIED_DOORS_SUMMARY_KEY, filterParams],
      queryFn: async ({ signal }) =>
        api.getOpportunityDoorsSummary(
          getTargetType('restaurant'),
          adaptUnifiedListParams(filterParams, getTargetType('restaurant')),
          signal
        ),
    }
  )

  return {
    enabled,
    getListAction: () => listAction,
    // The full stat-card set for the filtered doors, all from the unified doors
    // summary so the flag-on card never falls back to (or waits on) the slow,
    // unfiltered legacy `/reports/doors/summary/`. See DoorsTableComponentV2.
    unifiedCount: unifiedSummary?.count,
    unifiedChainsCount: unifiedSummary?.alt_count,
    unifiedLtv: unifiedSummary?.total_ltv,
    unifiedSummaryFetching,
  }
}
